export const PERMISSIONS = {
  /**
   * A donation is never removed. `cancelled` is terminal and the row stays, so
   * a receipt, an audit row or a reconciliation that points at it still
   * resolves, and `donation.backdate` gates entry for a day already past — see
   * docs/decisions/0022-the-donation-record.md and
   * docs/decisions/0027-donated-at-and-backdated-entry.md.
   *
   * `donation.delete` puts a donation still waiting into the trash, where the
   * row is kept and marked; `donation.viewDeleted` reads the trash, and
   * `donation.restore` puts one back as it was. Three permissions, because
   * reading records nobody else sees, taking a pledge off the books and
   * putting one back on are three different acts —
   * docs/decisions/0078-a-deleted-donation-is-kept-and-restored.md.
   *
   * `donation.recordForOperator` lets a privileged caller record an entry
   * against **another** operator — a supervisor typing up what a colleague took
   * on the telephone. It is its own permission because `created_by_user_id` is
   * what every accountability question reads, so naming somebody else there is
   * a different act from entering a donation,
   * and it is ceilinged by the rules that govern any other designation —
   * docs/decisions/0070-the-server-decides-four-fields-and-a-donation-names-its-parties.md.
   *
   * `donation.editQueued` is what a correction of a **queued** donation needs
   * beside `donation.edit`: once a donation is in the order it has left the
   * entering clerk's hands —
   * docs/decisions/0071-a-ready-donation-waits-and-the-order-locks-it.md.
   *
   * `donation.editLocked` corrects a donation the clerk who entered it no
   * longer may: one in the order, on an active PDF list, or carried out — the
   * last only for what neither prices nor groups it. A supervisor's act, held
   * by the three seeded supervisors and never derived from a staff assignment —
   * docs/decisions/0118-a-donation-is-edited-until-its-work-begins-and-money-locks-only-money.md.
   *
   * There is no `donation.approve`: nothing approves a donation since
   * docs/decisions/0077, and the seed deletes the permission and its grants.
   *
   * `donation.unfulfill` takes back a mistaken fulfilment — with a reason,
   * before the evidence was offered to anybody — and is a supervisor's act:
   * it is in no campaign staff assignment's derived set and no clerk's or
   * a fulfilment manager's role — docs/decisions/0091.
   *
   * `donation.voidPrintout` voids a PDF list, or takes one donation off it,
   * with a reason, so its donations may be printed onto another. Printing is
   * `donation.print`, which an assignment derives and a clerk holds; voiding
   * the paper trail is narrower — the supervisors and the fulfilment manager,
   * never an assignment — docs/decisions/0092.
   *
   * `donation.viewAllPrintoutLists` reads every PDF list the caller's scope
   * holds. Without it a list is read by its maker, or through the staff
   * assignment of its campaign at its institution — so a desk clerk and an
   * operations manager see their own lists and their assignments' lists, and
   * the supervisors, who hold it, see them all — docs/decisions/0097.
   *
   * `donation.import` enters a season's list of donations from the campaign's
   * own spreadsheet template, beside `donation.create`, which every imported
   * row is. It is its own permission because an import differs from a typed
   * entry in two places — it tells nobody, and it may enter a closed season
   * with `donation.backdate` — and no other caller may borrow either. It is
   * never derived from a staff assignment — docs/decisions/0104.
   *
   * `donation.setMainOrganization` is the upper authority's edit of the
   * organization a donation is mainly paid through (docs/decisions/0128): held
   * by `global_admin` and `region_manager`, never by a zone or an institution,
   * and judged against the donation's own scope.
   */
  donation: [
    'list',
    'view',
    'create',
    'edit',
    'editQueued',
    'editLocked',
    'backdate',
    'recordForOperator',
    'cancel',
    'delete',
    'viewDeleted',
    'restore',
    'queue',
    'fulfill',
    'unfulfill',
    'print',
    'voidPrintout',
    'viewAllPrintoutLists',
    'export',
    'import',
    'setMainOrganization',
  ],
  donationBox: ['list', 'view', 'create', 'edit', 'collect'],
  /**
   * Photos and videos of a fulfilment that arrive by themselves and are read for
   * their donation — docs/plans/05-finance-reconciliation.md §5.9. `match`
   * attaches one to a donation by hand and is what opens a medium's preview;
   * `archive` takes one out of the queue. None of them moves a donation.
   */
  inboundMedia: ['list', 'match', 'archive'],
  /**
   * `donor.export` and `donor.erase` are the two halves of KVKK article 11 and
   * GDPR articles 15 and 17, and neither rides on `donor.view` or `donor.edit`:
   * reading a donor's page is not the same act as carrying everything the
   * organization holds about them out of the system in one document, and
   * correcting a telephone number is not the same act as destroying an
   * identity. `donor.erase` is held by **two** people for one erasure to
   * happen at all — docs/decisions/0062.
   */
  donor: ['list', 'view', 'create', 'edit', 'merge', 'export', 'erase'],
  /**
   * Campaign management, and nothing else: no donation screen asks for these.
   * Entry, the donation lists, the queue and the trash read the campaign data
   * they need through `donation.campaigns.*`, behind their own donation
   * permission — docs/decisions/0088. Neither group has a `delete`, because a
   * campaign is archived and a category deactivated, both through `edit`.
   */
  campaign: ['list', 'view', 'create', 'edit', 'activate', 'assign'],
  campaignCategory: ['list', 'create', 'edit'],
  /**
   * Campaign staff assignments — which of an institution's own users work a
   * campaign there (docs/decisions/0089). Their own group, apart from
   * `campaign.assign`, which places a **season** at a yurt with its prices,
   * capabilities and managers: staffing an institution is a different act from
   * configuring where a season is executed, and is granted apart. `view` reads
   * the management list, `manage` adds, edits and removes. A person's own
   * assignments, read on their own screen, need neither, as `/profile` needs none.
   */
  campaignStaff: ['view', 'manage'],
  pledge: ['list', 'view', 'create', 'edit', 'cancel'],
  /**
   * `statement.delete` discards an **uncommitted** import and nothing else: a
   * committed statement's lines are the money that arrived and are never
   * deleted — docs/plans/05-finance-reconciliation.md §5.4.
   */
  statement: ['list', 'import', 'view', 'delete'],
  /**
   * `reconciliation.settings` sets the FX tolerance a cross-currency match may
   * absorb (docs/decisions/0128) — one number for the whole organization, so it
   * is global scope alone and held by `global_admin` only.
   */
  reconciliation: ['list', 'match', 'unmatch', 'approve', 'reverse', 'settings'],
  /**
   * `manualPayment.record` types a payment by hand against donations — a
   * transfer from its slip (*dekont*), a cheque, or cash counted at a desk or
   * out of a kumbara. It is its own permission because the people who take
   * such money are the region and zone managers as well as the accountant, and
   * recording it moves nothing: the donation is paid only when somebody else
   * holding `reconciliation.approve` approves it — docs/decisions/0100.
   */
  manualPayment: ['record'],
  receipt: ['list', 'issue', 'void', 'download'],
  refund: ['list', 'request', 'approve'],
  fundTransfer: ['list', 'create', 'approve', 'complete'],
  /**
   * `institutionVault.outflow` records money spent out of an institution's vault
   * and reverses a mistaken one — one person, effective at once, in the vault's
   * institution's scope (docs/decisions/0133). Reading the outflows needs only
   * `finance.view`, which opens *Kasalar*.
   */
  institutionVault: ['outflow'],
  /**
   * A rate is never edited — a wrong one is superseded by a later-dated row —
   * so there is no `edit`, and `backfill` gates both the dated import of past
   * bulletins and the re-run that fills donations left without a base amount:
   * docs/decisions/0019-exchange-rate-source.md.
   */
  exchangeRate: ['list', 'create', 'backfill'],
  /** Balances, the consolidation report and the ledger behind them. */
  finance: ['view'],
  /**
   * `lendAccount` decides whose bank balance a donor's money lands in: it lets
   * an operator draw a donation's payment link on **another** organization's
   * provider account, checked on that lending organization and in its own
   * scope. It is minted rather than folded into `organization.edit` because
   * lending an account to another legal entity is not an attribute of an
   * account — it must be grantable to somebody who may not configure
   * credentials and withholdable from everyone who edits donations —
   * docs/decisions/0053 *Amended 2026-09-17*.
   */
  organization: ['list', 'view', 'create', 'edit', 'lendAccount'],
  /**
   * `orgUnit.delete` removes a node nothing names; a node anything names is
   * deactivated through `orgUnit.edit` instead — docs/plans/02-identity-and-org.md §2.4.
   */
  orgUnit: ['list', 'view', 'create', 'edit', 'delete'],
  /**
   * `user.resetTwoFactor` clears another user's second factor and ends their
   * sessions, so their next sign-in lands on enrolment. It is granted to
   * `global_admin` and, like every `user.*` code, withheld from the deny-list
   * region and zone managers; a role may be given it on *Roller* —
   * docs/decisions/0135.
   */
  user: [
    'list',
    'view',
    'create',
    'edit',
    'deactivate',
    'manageRoles',
    'impersonate',
    'resetTwoFactor',
  ],
  role: ['list', 'view', 'create', 'edit', 'delete'],
  /**
   * `pseudonym.reveal` opens the real names of one session: every session starts
   * masked, and a holder proves their second factor to see the names the
   * pseudonyms and masks stand for — docs/decisions/0136. Held by `global_admin`
   * alone among the seeds; the deny-list region and zone managers exclude it
   * explicitly, and a role may be given it on *Roller*.
   */
  pseudonym: ['reveal'],
  report: ['view', 'export'],
  auditLog: ['list', 'export'],
  /**
   * The worker's operator screens: `view` reads failed jobs and the scheduled
   * run ledger, `manage` retries or dismisses a failure. Both are global scope
   * alone — a job serves every institution at once — and only `global_admin`
   * holds them; docs/decisions/0043-the-worker.md §6.
   */
  jobs: ['view', 'manage'],
  /**
   * The delivery record of every notification the system sent — who was told
   * what, on which channel, and what a provider answered. It is **global scope
   * alone**, as `jobs` is: one list carries donors' and staff addresses across
   * every institution at once, so a narrower caller holding it is refused
   * `OUT_OF_SCOPE` rather than shown a filtered list. A person's own
   * notifications and their own preferences need no permission, as `/profile`
   * needs none — docs/plans/07-notifications-and-reporting.md §7.1.
   */
  notification: ['view'],
  /**
   * The machines that may write to this system over a signed webhook —
   * docs/decisions/0065. **Global scope alone**, as `jobs` and `notification`
   * are: a client's reach is a scope of its own, so a caller who could create
   * one below global scope could hand a machine a reach they have not got.
   * `view` lists them with their fingerprints and never a secret; `manage`
   * creates one, replaces its signing key and turns it off.
   */
  integration: ['view', 'manage'],
  /**
   * Which entries of the admin sidebar a role shows — one key per leaf, and one
   * for the campaign entries the donation section lists from data
   * (docs/decisions/0124). A key **grants no screen**: a leaf is shown when its
   * key is held *and* its own data permission is, a route is guarded by the data
   * permission alone, and nothing in the API asks for a `menu.*` code. What a
   * campaign staff assignment derives shows its operations leaves without one.
   * A section or a group has no key: it is shown when one of its leaves is.
   *
   * Every seeded and existing role is given a key when it first appears, so
   * adding one changes nobody's menu; after that a role's keys are the
   * administrator's, and the seed never reconciles them.
   */
  menu: [
    'dashboard',
    'gallery',
    'operationCampaigns',
    'printoutLists',
    'inboundMedia',
    'donations',
    'donationCampaigns',
    'deletedDonations',
    'donationImports',
    'donationBoxes',
    'donors',
    'campaigns',
    'campaignCategories',
    'printoutTemplates',
    'campaignStaff',
    'pledges',
    'userCommitments',
    'financeGuide',
    'statementImports',
    'statementProfiles',
    'accountTransactions',
    'reconciliation',
    'reconciliationApprovals',
    'shortfalls',
    'payouts',
    'refunds',
    'fundTransfers',
    'institutionLedger',
    'balances',
    'consolidation',
    'receipts',
    'annualCertificates',
    'exchangeRates',
    'regions',
    'zones',
    'countries',
    'institutions',
    'organizations',
    'users',
    'roles',
    'generatedFiles',
    'reports',
    'auditLog',
    'jobFailures',
    'integrations',
    'scheduledRuns',
    'notifications',
  ],
} as const;

type PermissionMap = typeof PERMISSIONS;
type Resource = keyof PermissionMap;

export type PermissionResource = Resource;

export type Permission = {
  [R in Resource]: `${R}.${PermissionMap[R][number]}`;
}[Resource];

export const ALL_PERMISSIONS: Permission[] = Object.entries(PERMISSIONS).flatMap(
  ([resource, actions]) =>
    (actions as readonly string[]).map((action) => `${resource}.${action}` as Permission),
);

export type PermissionEntry = {
  code: Permission;
  resource: PermissionResource;
  action: string;
};

export type PermissionGroup = {
  resource: PermissionResource;
  permissions: readonly PermissionEntry[];
};

/**
 * The catalogue as the role editor renders it: one group per module, in the
 * declaration order above. Derived rather than duplicated, so a permission
 * added to `PERMISSIONS` cannot be missing from the matrix.
 */
export const PERMISSION_GROUPS: readonly PermissionGroup[] = Object.entries(PERMISSIONS).map(
  ([resource, actions]) => ({
    resource: resource as PermissionResource,
    permissions: (actions as readonly string[]).map((action) => ({
      code: `${resource}.${action}` as Permission,
      resource: resource as PermissionResource,
      action,
    })),
  }),
);

/** A sidebar entry's own key — docs/decisions/0124. */
export type MenuPermission = `menu.${PermissionMap['menu'][number]}`;

export const MENU_PERMISSIONS: readonly MenuPermission[] = PERMISSIONS.menu.map(
  (action): MenuPermission => `menu.${action}`,
);

/**
 * Whether a code decides what the sidebar shows rather than what a caller may
 * do. The seed reconciles a seeded role's other grants and never these.
 */
export function isMenuPermission(value: string): value is MenuPermission {
  return (MENU_PERMISSIONS as readonly string[]).includes(value);
}

export function isPermission(value: string): value is Permission {
  return (ALL_PERMISSIONS as string[]).includes(value);
}

export function hasPermission(granted: readonly Permission[], required: Permission): boolean {
  return granted.includes(required);
}
